import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Page } from "../../../components/Page";
import { TooltipLayer } from "./components/TooltipLayer";
import Spinner from "../../../components/Spinners";
import { ShowToast } from "../../../components/toast";
import { BackBar } from "./components/BackBar";
import { KpiTile } from "./components/KpiTile";
import { Chip } from "./components/Chip";
import { SectionCard } from "./components/SectionCard";
import { LoadError } from "./components/LoadError";
import { NoteModal } from "./components/modals/NoteModal";
import { thKeys, useEngines } from "./hooks/useTrackingHealth";
import { trackingHealth } from "../../../helpers/api/tracking-health";
import { evidenceSentence, fmtDate } from "./format";
import {
  ENGINE_PROFILE_GAP_DISPLAY,
  ENGINE_VERIFICATION_DISPLAY,
  PROBLEM_SHORT_LABELS,
  TRACKABILITY_UNPROVEN_DISPLAY,
  TRACKABILITY_YES_DISPLAY,
} from "./vocabulary";
import {
  ActionModal,
  FIELD_LABEL_CLASS,
  INPUT_CLASS,
} from "./components/modals/ActionModal";
import type { EngineRegistryRow } from "./types";

const TH_CLASS =
  "px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-[#F4F4F6] whitespace-nowrap";
const TD_CLASS = "px-4 py-3.5 border-b border-b-[#E9E9EB] text-sm align-top";

type EnginesTab = "registry" | "research";

/** §5.5: the red `No — 0 Healthy` chip has NO wire value behind it — it is
 *  rendered from the evidence counts, never from the two-value flag. */
function TrackabilityCell({ row }: { row: EngineRegistryRow }) {
  if (row.trackability === "known-trackable") {
    return <Chip tone="green">{TRACKABILITY_YES_DISPLAY}</Chip>;
  }
  const evidence = row.trackabilityEvidence;
  const healthy =
    evidence && typeof evidence["healthyHotels"] === "number"
      ? (evidence["healthyHotels"] as number)
      : null;
  const onEngine =
    evidence && typeof evidence["hotelsOnEngine"] === "number"
      ? (evidence["hotelsOnEngine"] as number)
      : row.hotelsOn;
  if (healthy === 0 && onEngine > 0) {
    // Proved: hotels run on it and zero are Healthy.
    return <Chip tone="red">No — 0 Healthy</Chip>;
  }
  // Nothing yet observed (or no evidence to prove otherwise).
  return <Chip tone="neutral">{TRACKABILITY_UNPROVEN_DISPLAY}</Chip>;
}

export function Engines() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: EnginesTab =
    searchParams.get("tab") === "research" ? "research" : "registry";

  const engines = useEngines();
  const [verificationTarget, setVerificationTarget] =
    useState<EngineRegistryRow | null>(null);
  const [noteTarget, setNoteTarget] = useState<EngineRegistryRow | null>(
    null,
  );

  const registry = engines.data?.registry ?? [];
  const researchQueue = engines.data?.researchQueue ?? [];

  const setParam = (patch: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setSearchParams(next, { replace: true });
  };

  return (
    <Page>
      <div className="p-4 md:h-[calc(100dvh-62px)] overflow-auto lg:h-[100dvh] bg-neutral-200 flex-1">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div>
            <BackBar
              crumbs={[
                { label: "Tracking Health", to: "/tracking/health" },
                { label: "Engines" },
              ]}
            />
            <h1 className="text-2xl font-semibold text-black mt-1">Engines</h1>
            <div className="text-sm text-content-secondary mt-1">
              Profiles live in code · dynamic state below
            </div>
          </div>
        </div>

        {engines.isLoading && (
          <div className="flex justify-center py-16">
            <Spinner className="!text-brand-primary" />
          </div>
        )}
        {engines.isError && (
          <LoadError
            error={engines.error}
            label="the engine registry"
            onRetry={() => engines.refetch()}
          />
        )}

        {engines.data && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <KpiTile label="Engines" value={engines.data.tiles.engines} />
              <KpiTile
                label="Known-trackable"
                value={engines.data.tiles.knownTrackable}
                numberClassName="text-success-dark"
              />
              <KpiTile
                label="Provisional"
                value={engines.data.tiles.provisional}
              />
            </div>

            {/* Tabs */}
            <div className="flex items-center border-b border-[#E9E9EB] mb-3">
              {(
                [
                  { key: "registry", label: `Registry (${registry.length})` },
                  {
                    key: "research",
                    label: `Research queue (${researchQueue.length})`,
                  },
                ] as Array<{ key: EnginesTab; label: string }>
              ).map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setParam({ tab: key === "registry" ? "" : key })}
                  className={clsx(
                    "px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors",
                    tab === key
                      ? "border-brand-primary text-content-primary"
                      : "border-transparent text-content-secondary hover:text-content-primary",
                  )}
                >
                  {label}
                </button>
              ))}
            </div>

            {tab === "registry" ? (
              <RegistryTable
                rows={registry}
                onLogVerification={setVerificationTarget}
                onLogNote={setNoteTarget}
              />
            ) : (
              <ResearchQueueTable rows={researchQueue} />
            )}


            {noteTarget && (
              <LogEngineNoteModal
                row={noteTarget}
                onClose={() => setNoteTarget(null)}
              />
            )}

            {verificationTarget && (
              <LogVerificationModal
                row={verificationTarget}
                onClose={() => setVerificationTarget(null)}
              />
            )}
          </>
        )}
      </div>
      <TooltipLayer />
    </Page>
  );
}

// ─── Registry table ───────────────────────────────────────────────────────────

function RegistryTable({
  rows,
  onLogVerification,
  onLogNote,
}: {
  rows: EngineRegistryRow[];
  onLogNote: (row: EngineRegistryRow) => void;
  onLogVerification: (row: EngineRegistryRow) => void;
}) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-[#E9E9EB] overflow-auto">
      <table className="w-full">
        <thead>
          <tr>
            <th className={`${TH_CLASS} rounded-tl-lg`}>Engine</th>
            <th className={TH_CLASS}>Source</th>
            <th className={TH_CLASS}>Hotels</th>
            <th className={TH_CLASS}>Known-trackable</th>
            <th className={TH_CLASS}>Registry gaps</th>
            <th className={`${TH_CLASS} rounded-tr-lg`}>Last verification</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.engineKey} className="hover:bg-gray-50 transition-colors">
              <td className={TD_CLASS}>
                <span className="font-medium text-content-primary">
                  {row.displayName}
                </span>
              </td>
              <td className={TD_CLASS}>
                <Chip tone="neutral">
                  {row.kind === "enum-backed" ? "enum" : "provisional"}
                </Chip>
              </td>
              <td className={TD_CLASS}>{row.hotelsOn}</td>
              <td className={TD_CLASS}>
                <TrackabilityCell row={row} />
              </td>
              <td className={TD_CLASS}>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {/* Issue 41: the registry entry ITSELF is the problem —
                      no hotel has failed yet, but every hotel provisioned
                      onto the engine is set up to. Derived from code per
                      read; null (provisional) renders nothing. An open
                      engine-investigation chip led this cell until
                      2026-08-14 (backend ADR 0022). */}
                  {row.profileGaps?.length === 0 && (
                    <span className="text-gray-400">—</span>
                  )}
                  {(row.profileGaps ?? []).map((gap) => (
                    <Chip key={gap} tone="orange">
                      {ENGINE_PROFILE_GAP_DISPLAY[gap] ?? gap}
                    </Chip>
                  ))}
                </div>
              </td>
              <td className={TD_CLASS}>
                <div className="flex items-center gap-2 flex-wrap">
                  {row.lastVerification ? (
                    <span className="text-content-secondary">
                      {ENGINE_VERIFICATION_DISPLAY[row.lastVerification.kind] ??
                        row.lastVerification.kind}{" "}
                      {fmtDate(row.lastVerification.at)}
                    </span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                  <button
                    onClick={() => onLogVerification(row)}
                    className="text-xs font-medium text-brand-primary hover:text-brand-primary-hover"
                    data-tip="Record an attended session or an outbound vendor message — the two kinds nothing else records"
                  >
                    + log
                  </button>
                  <button
                    onClick={() => onLogNote(row)}
                    className="text-xs font-medium text-gray-500 hover:text-content-primary"
                    data-tip="Note something about this engine that is not a verification — a chase, a status you were told"
                  >
                    + note
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && (
        <div className="p-10 text-center text-sm text-gray-400">
          No engines on record yet.
        </div>
      )}
    </div>
  );
}

// ─── Research queue tab ───────────────────────────────────────────────────────

function ResearchQueueTable({ rows }: { rows: EngineRegistryRow[] }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-[#E9E9EB] overflow-auto">
      <table className="w-full">
        <thead>
          <tr>
            <th className={`${TH_CLASS} rounded-tl-lg`}>Engine</th>
            <th className={TH_CLASS}>Implementation method</th>
            <th className={TH_CLASS}>Last verification attempt</th>
            <th className={TH_CLASS}>Traffic level</th>
            <th className={`${TH_CLASS} rounded-tr-lg`}>Next follow-up</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.engineKey} className="hover:bg-gray-50 transition-colors">
              <td className={TD_CLASS}>
                <span className="font-medium text-content-primary">
                  {row.displayName}
                </span>
              </td>
              <td className={TD_CLASS}>
                {row.researchQueue?.implementationMethod ?? (
                  <span className="text-gray-400">—</span>
                )}
              </td>
              <td className={TD_CLASS}>
                {fmtDate(row.researchQueue?.lastVerificationAttemptAt)}
              </td>
              <td className={TD_CLASS}>
                {row.researchQueue?.trafficLevel ?? (
                  <span className="text-gray-400">—</span>
                )}
              </td>
              <td className={TD_CLASS}>
                {fmtDate(row.researchQueue?.nextFollowUpOn)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && (
        <div className="p-10 text-center text-sm text-gray-400">
          The research queue is empty — no provisional engines await
          investigation.
        </div>
      )}
    </div>
  );
}

// ─── Engine note modal (backend issue 39) ────────────────────────────────────
//
// An `InvestigationCard` stood here until 2026-08-14: it read one engine's
// shared investigations, listed the hotel incidents linked to each, showed the
// shared blocked-on note with its next-reminder date, and wrote that note
// through the engine-scoped endpoint — one entry resetting every linked
// hotel's reminder clock. Backend ADR 0022 removed the shared investigation
// it read, so what remains is the note itself: a record that a person did or
// learned something about this ENGINE, moving no hotel's clock.

function LogEngineNoteModal({
  row,
  onClose,
}: {
  row: EngineRegistryRow;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const logNote = useMutation({
    mutationFn: (note: string) =>
      trackingHealth.logEngineNote(row.engineKey, { note }),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Note logged" });
      queryClient.invalidateQueries({ queryKey: thKeys.engines });
      onClose();
    },
    onError: (error: any) =>
      ShowToast({
        type: "error",
        message:
          error?.response?.data?.message ??
          error?.message ??
          "Failed to log note",
      }),
  });

  return (
    <NoteModal
      title={`Note — ${row.displayName}`}
      helper="Recorded against the engine, not a hotel. It is not a verification, and it moves no hotel's reminder clock."
      resetsReminder={false}
      pending={logNote.isPending}
      onClose={onClose}
      onSubmit={(note) => logNote.mutate(note)}
    />
  );
}

// ─── Log verification modal (backend issue 40) ───────────────────────────────

const VERIFICATION_KINDS = [
  {
    kind: "attended-session" as const,
    label: "Attended session",
    hint: "a live session with the vendor or a customer where the engine's tracking was actually observed (ADR 0014)",
  },
  {
    kind: "message-sent" as const,
    label: "Message sent",
    hint: "an outbound message to the engine vendor — the chase itself, so “emailed three weeks ago, heard nothing” stops living in someone's memory",
  },
];

function LogVerificationModal({
  row,
  onClose,
}: {
  row: EngineRegistryRow;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<"attended-session" | "message-sent">(
    "attended-session",
  );
  const [note, setNote] = useState("");

  const log = useMutation({
    mutationFn: () =>
      trackingHealth.logEngineVerification(row.engineKey, {
        kind,
        note: note.trim(),
      }),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Verification recorded" });
      queryClient.invalidateQueries({ queryKey: thKeys.engines });
      onClose();
    },
    onError: (error: any) =>
      ShowToast({
        type: "error",
        message:
          error?.response?.data?.message ?? "Failed to record verification",
      }),
  });

  return (
    <ActionModal
      title={`Log a verification — ${row.displayName}`}
      submitLabel="Record"
      onSubmit={() => log.mutate()}
      onClose={onClose}
      pending={log.isPending}
      disabled={!note.trim()}
    >
      <p className="text-sm text-gray-500">
        The two verification kinds nothing automated records. A blocked-on
        note is a chase, not a verification — log those on the investigation
        instead.
      </p>
      <div>
        <label className={FIELD_LABEL_CLASS}>Kind</label>
        <div className="flex flex-col gap-1.5">
          {VERIFICATION_KINDS.map((option) => (
            <label
              key={option.kind}
              className="flex items-start gap-2 text-sm text-content-primary cursor-pointer"
            >
              <input
                type="radio"
                name="verification-kind"
                checked={kind === option.kind}
                onChange={() => setKind(option.kind)}
                className="mt-0.5 accent-brand-primary"
              />
              <span>
                {option.label}
                <span className="block text-xs text-gray-400">
                  {option.hint}
                </span>
              </span>
            </label>
          ))}
        </div>
      </div>
      <div>
        <label className={FIELD_LABEL_CLASS}>What was learned</label>
        <textarea
          className={`${INPUT_CLASS} min-h-[88px]`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Mandatory — a verification without what-was-learned is a timestamp nobody can act on"
          autoFocus
        />
      </div>
    </ActionModal>
  );
}
